import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { BehaviorSubject, interval, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { DialogConnectedUsersComponent } from '../../dialog/dialog-connected-users.component';
import { DialogService } from '../../dialog/dialog.service';
import { UserService } from '../../../users/users.service';
import { UserStats } from '../../../users/users.model';
import { CardTemplateConfig } from '../card-template/card-template.component';

const REFRESH_INTERVAL_MS = 60000;

@Component({
  selector: 'app-auction-connected-users',
  templateUrl: './auction-connected-users.component.html',
  styleUrls: ['./auction-connected-users.component.scss'],
})
export class AuctionConnectedUsersComponent implements OnInit, OnDestroy, OnChanges {
  @Input() auctionId: string | null = null;
  @Input() fillHeight = false;

  readonly usersData$ = new BehaviorSubject<UserStats | null>(null);
  readonly isLoading$ = new BehaviorSubject<boolean>(false);
  readonly error$ = new BehaviorSubject<string | null>(null);

  timerSeconds = REFRESH_INTERVAL_MS / 1000;

  private readonly destroy$ = new Subject<void>();

  get cardConfig(): CardTemplateConfig {
    return {
      title: 'Usuarios conectados',
      cardClass: this.fillHeight ? 'auction-connected-users-card fill-card' : 'auction-connected-users-card',
      compact: true,
    };
  }

  constructor(
    private readonly dialog: DialogService,
    private readonly userService: UserService,
  ) {}

  ngOnInit(): void {
    this.startAutoRefresh();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['auctionId']?.currentValue) {
      this.loadConnectedUsers();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onRefresh(): void {
    this.resetTimer();
    this.loadConnectedUsers();
  }

  onClientsStatClick(clients: number): void {
    if (!this.auctionId || clients <= 0) {
      return;
    }
    this.loadConnectedUsers();
    this.dialog.host.open(DialogConnectedUsersComponent, {
      disableClose: true,
      width: '70%',
      data: {
        auctionId: this.auctionId,
      },
    });
  }

  getClientsStatClass(clients: number): Record<string, boolean> {
    return {
      'clients--clickable': clients > 0,
      'clients--empty': clients <= 0,
    };
  }

  loadConnectedUsers(): void {
    if (!this.auctionId) {
      return;
    }

    this.isLoading$.next(true);
    this.error$.next(null);

    this.userService
      .getSessions(this.auctionId)
      .then((users) => {
        this.usersData$.next(users);
      })
      .catch((error) => {
        this.error$.next('Error cargando usuarios conectados');
        console.error('Error loading connected users:', error);
      })
      .finally(() => {
        this.isLoading$.next(false);
      });
  }

  private startAutoRefresh(): void {
    this.resetTimer();

    interval(1000)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.timerSeconds--;
        if (this.timerSeconds <= 0) {
          this.resetTimer();
          this.loadConnectedUsers();
        }
      });
  }

  private resetTimer(): void {
    this.timerSeconds = Math.floor(REFRESH_INTERVAL_MS / 1000);
  }
}
